import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const STAT_LABEL = {
  pending:     "Pending",
  processing:  "In Flight",
  delivered:   "Delivered",
  retry:       "Awaiting Retry",
  dead_letter: "Failed",
};

const STAT_COLOR = {
  pending:     { bg: "#F4F6F8", fg: "#525252", bd: "#E5E5E5" },
  processing:  { bg: "#EEF2FA", fg: "#25467A", bd: "#94ABD8" },
  delivered:   { bg: "#E4F5EA", fg: "#1E7C3B", bd: "#34C759" },
  retry:       { bg: "#FFF5DB", fg: "#946200", bd: "#F5C34A" },
  dead_letter: { bg: "#FDECEC", fg: "#B4232C", bd: "#EF6767" },
};

const KEYS = ["pending", "processing", "retry", "delivered", "dead_letter"];

export default function IntegrationsOutboxStatus() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/integrations/outbox/stats");
      setStats(data || { counts: {} });
    } catch (e) {
      setError("Failed to load outbox status");
      setStats(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  const counts = (stats && stats.counts) || {};

  return (
    <div className="border border-black/10 p-6 mt-6" data-testid="outbox-status-card">
      <div className="flex items-center justify-between mb-4">
        <div className="fx-mono-tag">OUTBOUND EVENTS · n8n</div>
        <button
          type="button"
          onClick={refresh}
          className="text-xs font-mono uppercase tracking-widest text-[#0B4DCC] hover:underline"
          data-testid="outbox-refresh-btn"
        >
          Refresh
        </button>
      </div>
      <p className="text-xs text-neutral-600 mb-4 max-w-lg">
        Durable outbox for project events sent to n8n. Delivery is at-least-once
        with retry + dead-letter. Nothing is delivered while your n8n connection
        is disabled or unconfigured — events wait safely.
      </p>

      {loading ? (
        <div className="text-sm text-neutral-500" data-testid="outbox-loading">Loading…</div>
      ) : error ? (
        <div className="text-sm text-red-700" data-testid="outbox-error">{error}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {KEYS.map((k) => {
            const c = STAT_COLOR[k];
            const value = Number.isFinite(counts[k]) ? counts[k] : 0;
            return (
              <div key={k}
                   className="border rounded-md p-3"
                   style={{ background: c.bg, borderColor: c.bd }}
                   data-testid={`outbox-count-${k}`}>
                <div className="text-[10px] font-mono uppercase tracking-widest"
                     style={{ color: c.fg }}>
                  {STAT_LABEL[k]}
                </div>
                <div className="text-2xl font-semibold tracking-tight mt-1"
                     style={{ color: c.fg }}>
                  {value}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {stats && stats.connection && (
        <div className="mt-4 text-xs text-neutral-600" data-testid="outbox-conn-state">
          Connection:{" "}
          <code className="text-xs">{stats.connection.state}</code>
          {stats.connection.last_delivered_at && (
            <>
              {"  ·  Last delivered:"}{" "}
              <code className="text-xs">{stats.connection.last_delivered_at}</code>
            </>
          )}
        </div>
      )}
    </div>
  );
}
